import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { cachedGet } from '../utils/apiCache';
import { renderBold } from '../utils/markdownText';
import './Syllabus.css';

const StructureNode = ({ node, lawName, depth }) => {
  const labelHeader = {
    Titulo: depth === 0 ? `Título ${node.number}` : node.number,
    Capitulo: `Capítulo ${node.number}`,
    Seccion: `Sección ${node.number}ª`,
  }[node.label] || node.number;

  return (
    <div className={`struct-node struct-${node.label.toLowerCase()}`}>
      <h4 className="struct-title">
        <span className="struct-tag">{labelHeader}</span>
        <span className="struct-name">{node.name}</span>
      </h4>

      {node.articles && node.articles.length > 0 && (
        <div className="article-pills">
          {node.articles.map((a) => (
            <Link
              key={a}
              to={`/article/${encodeURIComponent(lawName)}/${encodeURIComponent(a)}`}
              className="article-pill"
            >
              {a}
            </Link>
          ))}
        </div>
      )}

      {node.children && node.children.length > 0 && (
        <div className="struct-children">
          {node.children.map((c, i) => (
            <StructureNode key={i} node={c} lawName={lawName} depth={depth + 1} />
          ))}
        </div>
      )}
    </div>
  );
};

const Syllabus = () => {
  const { oppositionName } = useParams();
  const navigate = useNavigate();
  const [syllabus, setSyllabus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [tipsByLesson, setTipsByLesson] = useState({}); // {lessonName: [...tips]}

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    // El temario (~300 KB) no cambia salvo que se recargue el temario o se
    // vuelva a generar la caché en el servidor (algo que solo hace un admin
    // desde el backend). cachedGet evita redescargarlo entero cada vez que
    // se entra a esta pantalla dentro de la misma sesión.
    cachedGet(`syllabus:${oppositionName}`, () => axios.get(`/api/syllabus/${oppositionName}`))
      .then((response) => { if (!cancelled) setSyllabus(response.data); })
      .catch((error) => console.error('Error fetching syllabus', error))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [oppositionName]);

  const fetchTipsForLesson = async (lessonName) => {
    if (tipsByLesson[lessonName] !== undefined) return; // ya cargado o vacío
    try {
      const r = await axios.get(
        `/api/lessons/${encodeURIComponent(oppositionName)}/${encodeURIComponent(lessonName)}/tips`
      );
      setTipsByLesson((prev) => ({ ...prev, [lessonName]: r.data }));
    } catch (err) {
      setTipsByLesson((prev) => ({ ...prev, [lessonName]: [] }));
    }
  };

  const toggle = (idx, lessonName) => {
    setExpanded((s) => ({ ...s, [idx]: !s[idx] }));
    if (!expanded[idx]) fetchTipsForLesson(lessonName);
  };

  if (loading) return <div className="page-loader">Cargando temario…</div>;

  return (
    <div>
      <button
        onClick={() => navigate(`/oposicion/${oppositionName}`)}
        className="back-link"
      >
        ← Volver
      </button>

      <header className="page-header">
        <h1 className="page-title">Temario de {oppositionName}</h1>
        <p className="page-subtitle">
          {syllabus.length} tema{syllabus.length === 1 ? '' : 's'}
        </p>
      </header>

      {syllabus.length === 0 ? (
        <div className="empty-state">
          <p>No hay temario disponible para esta oposición.</p>
        </div>
      ) : (
        <div className="lesson-list">
          {syllabus.map((lessonItem, index) => {
            const isOpen = !!expanded[index];
            return (
              <div key={index} className={`lesson ${isOpen ? 'open' : ''}`}>
                <button
                  type="button"
                  className="lesson-header"
                  onClick={() => toggle(index, lessonItem.lesson)}
                >
                  <span className="lesson-title">{lessonItem.lesson}</span>
                  <span className="lesson-chevron">{isOpen ? '▾' : '▸'}</span>
                </button>

                {isOpen && (
                  <div className="lesson-body">
                    {lessonItem.desc && (
                      <p className="lesson-desc">{lessonItem.desc}</p>
                    )}

                    {tipsByLesson[lessonItem.lesson] && tipsByLesson[lessonItem.lesson].length > 0 && (
                      <div className="tips-block">
                        <h4 className="tips-title">💡 Consejos</h4>
                        <ul className="tips-list">
                          {tipsByLesson[lessonItem.lesson].map((t) => (
                            <li key={t.id} className={`tip-item tip-${t.source}`}>
                              <span className="tip-text">{renderBold(t.text, t.id)}</span>
                              {t.source === 'general' && <span className="tip-tag">general</span>}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {(lessonItem.laws || []).map((lawItem, lawIndex) => (
                      <div key={lawIndex} className="law-block">
                        <h3 className="law-name">{lawItem.law}</h3>

                        {lawItem.structure && lawItem.structure.length > 0 ? (
                          <div className="struct-root">
                            {lawItem.structure.map((node, ni) => (
                              <StructureNode
                                key={ni}
                                node={node}
                                lawName={lawItem.law}
                                depth={0}
                              />
                            ))}
                          </div>
                        ) : (
                          <div className="article-pills">
                            {lawItem.articles.map((article) => (
                              <Link
                                key={article}
                                to={`/article/${encodeURIComponent(lawItem.law)}/${encodeURIComponent(article)}`}
                                className="article-pill"
                              >
                                {article}
                              </Link>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Syllabus;
